import React, { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../redux/store";
import { changeOrderView, getOrders } from "../../../redux/slices/orders-slice";
import { OrderViewEnum } from "../enums/order/view.enum";
import OrderCards from "./order-cards";
import { OrderModel } from "../../shared/models/order/order-item.model";
import { matchRoutes, useLocation, useNavigate, useOutletContext } from "react-router-dom";
import { ActionbarType } from "../../../redux/reducers/actionbar-reducers";
import { changeActionbarType } from "../../../redux/slices/actionbar.slice";
import { changeSidebarInOrders, changeSidebarWithNavigate } from "../../../redux/slices/sidebar.slice";
import OrderList from "./order-list/order-list";
import { BarekamColumns, OrdersTableColumn } from "./models/column-models";
import { ActionBar } from "../../components/action-bar/action-bar";
import { Message, MessageSections } from "../../components/message/message";
import { Recipient } from "../../shared/models/user/recipient/recipient";
import { StandardDialog } from "../../components/dialogs/standard-dialog/standard-dialog";
import { AddIdentityDocument } from "../add-identity-documents/add-identity-document";
import { getMyRecipients } from "../../../redux/slices/recipients.slice";
import { OutletContext } from "../../../router/router";
import BarekamText from "../../components/text";
import { BarekamIcon, IconVariants } from "../../components/icon/barekam-icon";
import { ToastContainer } from "react-toastify";
import { OrderIcons } from "../../components/icon/variants";
import { WarningDialog } from "../../components/dialogs/warning-dialog";
import { OrderStatesEnum } from "../enums/order-states.enum";
import { getCurrentUserData } from "../../../redux/slices/current-user.slice";

const internalTrackingNumberColumn: OrdersTableColumn = { field: 'internalTrackingNumber', headerName: 'order_list_barekam_tracking', variant: BarekamColumns.barekamTrack };
const productTrackNumbersColumn: OrdersTableColumn = { field: 'products', headerName: 'order_list_tracking', variant: BarekamColumns.productTrackNumbers };
const nameColumn: OrdersTableColumn = { field: 'name', headerName: 'order_list_name', variant: BarekamColumns.text };
const shippingInfoColumn: OrdersTableColumn = { field: 'shippingInfo', headerName: 'order_list_sender', variant: BarekamColumns.sender, removeInTablet: true };
const shippingCategoryColumn: OrdersTableColumn = { field: 'shippingInfo', headerName: 'order_list_shipping_category', variant: BarekamColumns.shippingCategory, removeInTablet: true };
const paymentInfoColumn: OrdersTableColumn = { field: 'paymentInfo', headerName: 'order_list_price', variant: BarekamColumns.money, removeInTablet: true };
const paymentStatusColumn: OrdersTableColumn = { field: 'paymentStatus', headerName: 'order_list_payment', variant: BarekamColumns.paymentStatus, removeInTablet: true };
const recipientColumn: OrdersTableColumn = { field: 'recipient', headerName: 'order_list_recipient', variant: BarekamColumns.recipient, removeInMobile: true };
const expectedDateColumn: OrdersTableColumn = { field: 'expectedDate', headerName: 'order_list_date', variant: BarekamColumns.date, removeInMobile: true }
const deliveredDateColumn: OrdersTableColumn = { field: 'updatedOn', headerName: 'order_delivered_date', variant: BarekamColumns.deliveredDate, removeInMobile: true }
// const additionalServicesColumn: OrdersTableColumn = {
//     field: 'additionalService',
//     headerName: 'order_list_additional_services',
//     variant: BarekamColumns.additionalServices,
//     removeInTablet: true
// };
const actionsColumn: OrdersTableColumn = { field: '', headerName: 'order_list_actions', variant: BarekamColumns.actions, removeInMobile: true }

export const Orders = () => {
    const [isReady, setIsReady] = useState<boolean>(false);
    const [columns, setColumns] = useState<OrdersTableColumn[]>([]);

    const [mainRecipient, setMainRecipient] = useState<Recipient | undefined>();
    const [addIdentityDocumentOpen, setAddIdentityDocumentOpen] = useState(false);
    const [isPopupOpened, setIsPopupOpened] = useState(false);
    const [isPaymentSuccessfulPopupOpened, setIsPaymentSuccessfulPopupOpened] = useState(false);

    const { ordersData, orderViewType } = useAppSelector(state => state.ordersReducer);
    const { matchesPhone } = useAppSelector(state => state.deviceReducer);
    const { orderStateId } = useAppSelector(state => state.ordersReducer);
    const { myRecipientsData } = useAppSelector(state => state.recipientDataReducer);
    const { orderStates } = useAppSelector(state => state.staticDataReducer);

    const outletContext = useOutletContext<OutletContext>();

    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        const matches = matchRoutes([{ path: '/:language/orders/:orderId' }], location);

        setIsPopupOpened(matches
            ? matches.some(match => match.params.orderId ? !isNaN(+match.params.orderId) : false)
            : false
        )
    }, [location])

    useEffect(() => {
        switch (orderStateId) {
            case OrderStatesEnum.entered:
                setColumns([
                    nameColumn,
                    productTrackNumbersColumn,
                    shippingCategoryColumn,
                    shippingInfoColumn,
                    paymentInfoColumn,
                    recipientColumn,
                    actionsColumn
                ])
                break;
            case OrderStatesEnum.inWarehouse:
                setColumns([
                    nameColumn,
                    internalTrackingNumberColumn,
                    productTrackNumbersColumn,
                    shippingCategoryColumn,
                    shippingInfoColumn,
                    paymentInfoColumn,
                    recipientColumn,
                    expectedDateColumn
                ])
                break;
            case OrderStatesEnum.onTheWay:
                setColumns([
                    nameColumn,
                    internalTrackingNumberColumn,
                    productTrackNumbersColumn,
                    shippingCategoryColumn,
                    shippingInfoColumn,
                    paymentInfoColumn,
                    paymentStatusColumn,
                    recipientColumn,
                    expectedDateColumn,
                    actionsColumn
                ])
                break;
            case OrderStatesEnum.inLocalWarehouse:
                setColumns([
                    nameColumn,
                    internalTrackingNumberColumn,
                    productTrackNumbersColumn,
                    shippingCategoryColumn,
                    shippingInfoColumn,
                    paymentInfoColumn,
                    paymentStatusColumn,
                    // trackRecordColumn,
                    recipientColumn,
                    actionsColumn
                ])
                break;
            case OrderStatesEnum.readyForPickup:
                setColumns([
                    nameColumn,
                    internalTrackingNumberColumn,
                    productTrackNumbersColumn,
                    shippingCategoryColumn,
                    shippingInfoColumn,
                    paymentInfoColumn,
                    paymentStatusColumn,
                    // trackRecordColumn,
                    recipientColumn,
                    actionsColumn
                ])
                break;
            case OrderStatesEnum.received:
                setColumns([
                    nameColumn,
                    internalTrackingNumberColumn,
                    productTrackNumbersColumn,
                    shippingCategoryColumn,
                    shippingInfoColumn,
                    paymentInfoColumn,
                    deliveredDateColumn,
                    recipientColumn
                ])
                break;
        }
    }, [orderStateId])

    useEffect(() => {
        dispatch(changeSidebarInOrders(true));
        dispatch(changeSidebarWithNavigate(false));
        dispatch(changeActionbarType(ActionbarType.ORDERS));

        if (matchesPhone) {
            dispatch(changeOrderView(OrderViewEnum.cards));
        }

        if (!isReady) {
            dispatch(getOrders({ page: ordersData.currentPage || 0 }))
                .then(() => setIsReady(true));
        }
    }, [dispatch])

    useEffect(() => {
        setMainRecipient(myRecipientsData.recipients.find(recipient => recipient.isMain))
    }, [])

    useEffect(() => {
        if (addIdentityDocumentOpen) {
            setAddIdentityDocumentOpen(false);

            const mainRecipient = myRecipientsData.recipients.find(recipient => recipient.isMain);

            if (mainRecipient?.passportOrIdSerialNumber) {
                dispatch(getCurrentUserData()).then(() => {
                    navigate('../new')
                });
            }
        }
    }, [myRecipientsData.recipients]);

    const ActionBarText = () => {
        const state = orderStates.find(state => state.orderStateId === orderStateId);

        return (
            !isPopupOpened && state
                ? <Message messageKey={state.name} sectionId={MessageSections.ORDERS} />
                : <></>
        )
    }

    function onNewOrderClick() {
        if (outletContext.hasValidatedRecipient) {
            navigate('../new')
        } else {
            setAddIdentityDocumentOpen(true);
        }
    }

    function onRecipientIdentityPopupSave(recipient: Recipient | null) {
        if (!!recipient) {
            dispatch(getMyRecipients({ forceLoad: true }))
        } else {
            setAddIdentityDocumentOpen(false);
        }
    }

    function onOpen(row: OrderModel) {
        navigate(`../${row.orderId}`)
    }

    function onSuccessfulPayment(isSucceeded: boolean) {
        if (isSucceeded) {
            setIsPaymentSuccessfulPopupOpened(true);

            dispatch(getOrders({ page: ordersData.currentPage || 0 }))
                .then(() => setIsReady(true));
        }
    }

    return (
        <>
            <ActionBar buttonText={<>
                <BarekamIcon variant={IconVariants.plus} size={{ width: '1rem', height: '1rem' }} />

                <BarekamText variant={'m'} fontWeight={700}>
                    <Message messageKey={'add_new_order'} sectionId={MessageSections.ORDERS} />
                </BarekamText>
            </>}
                headerText={<ActionBarText />}
                buttonFunction={onNewOrderClick} />

            {
                orderViewType === OrderViewEnum.list
                    ? <OrderList ordersData={ordersData} openAction={onOpen}
                        setPaymentPopupOpened={onSuccessfulPayment} columns={columns}
                        isWarehouseWorker={outletContext.isWarehouseWorker || outletContext.isLocalWarehouseWorker} />
                    : <OrderCards ordersData={ordersData} openAction={onOpen}
                        setPaymentPopupOpened={onSuccessfulPayment} columns={columns}
                        isWarehouseWorker={outletContext.isWarehouseWorker || outletContext.isLocalWarehouseWorker} />
            }

            <StandardDialog headerLabel={<BarekamText variant={'l'} bold><Message messageKey={'add_identity_document'} sectionId={MessageSections.USER} /></BarekamText>}
                open={addIdentityDocumentOpen}>
                {mainRecipient &&
                    <AddIdentityDocument recipient={mainRecipient}
                        recipientSaved={onRecipientIdentityPopupSave}
                        popupClosed={() => setAddIdentityDocumentOpen(false)} />
                }
            </StandardDialog>

            {/*<ConfirmationDialog open={dialogOpen}*/}
            {/*                    handleClose={handleClose}*/}
            {/*                    titleKey={dialogTitle}*/}
            {/*                    descriptionKey={'dialogDescription'}/>  */}

            <ToastContainer />

            <WarningDialog open={isPaymentSuccessfulPopupOpened}
                handleClose={() => {
                    setIsPaymentSuccessfulPopupOpened(false);
                }}
                titleKey={'paid_successfully'}
                descriptionIcon={<BarekamIcon variant={OrderIcons.paymentSuccessful} />}
                descriptionKey={'paid_successfully_description'} />
        </>
    )
}
